import { useEffect } from 'react';

import type {
  EmploymentType,
  StorefrontJobOpening,
  WorkplaceType,
} from '@/features/storefront/api/careers';
import type { ContentReader } from '@/features/storefront/content/useContent';
import { richTextToPlain } from '@/lib/richText';

/**
 * The rules about job openings that the list page, the role page and the
 * careers section all need - what a type is called, where "Apply" goes, how
 * a closing date reads - kept once, so the three cannot disagree.
 */

const EMPLOYMENT_KEYS: Record<EmploymentType, string> = {
  FULL_TIME: 'careers.type_full_time',
  PART_TIME: 'careers.type_part_time',
  CONTRACT: 'careers.type_contract',
  INTERNSHIP: 'careers.type_internship',
  TEMPORARY: 'careers.type_temporary',
  VOLUNTEER: 'careers.type_volunteer',
};

const WORKPLACE_KEYS: Record<WorkplaceType, string> = {
  ON_SITE: 'careers.workplace_on_site',
  REMOTE: 'careers.workplace_remote',
  HYBRID: 'careers.workplace_hybrid',
};

export function employmentTypeLabel(copy: ContentReader, type: EmploymentType): string {
  return copy(EMPLOYMENT_KEYS[type]);
}

export function workplaceTypeLabel(copy: ContentReader, type: WorkplaceType): string {
  return copy(WORKPLACE_KEYS[type]);
}

/**
 * The short facts a shopper scans a role by, in reading order: where it sits,
 * where it is, how it is held, and what it pays. Blank ones are left out.
 */
export function openingChips(
  copy: ContentReader,
  opening: Pick<
    StorefrontJobOpening,
    'department' | 'location' | 'employment_type' | 'workplace_type' | 'salary_text'
  >,
): string[] {
  return [
    opening.department,
    opening.location,
    employmentTypeLabel(copy, opening.employment_type),
    workplaceTypeLabel(copy, opening.workplace_type),
    opening.salary_text,
  ].filter((value): value is string => Boolean(value && value.trim()));
}

/**
 * Where "Apply" goes: the merchant's own application form when they gave one,
 * else an email to them with the role in the subject. `null` when neither -
 * the page then says to get in touch rather than drawing a dead button.
 */
export function applyLink(
  opening: Pick<StorefrontJobOpening, 'apply_url' | 'apply_email' | 'title'>,
  subject: string,
): { href: string; external: boolean } | null {
  const url = opening.apply_url?.trim();
  if (url && /^https?:\/\//i.test(url)) return { href: url, external: true };
  const email = opening.apply_email?.trim();
  if (email) {
    return { href: `mailto:${email}?subject=${encodeURIComponent(subject)}`, external: false };
  }
  return null;
}

/**
 * The role's meta description: its summary, else the start of its
 * description as plain words - cut at a word, never mid-tag.
 */
export function jobMetaDescription(
  opening: Pick<StorefrontJobOpening, 'summary' | 'description'>,
  max = 160,
): string | undefined {
  const summary = opening.summary?.trim();
  if (summary) return summary;
  const text = richTextToPlain(opening.description).replace(/\s+/g, ' ').trim();
  if (!text) return undefined;
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/**
 * Where "Apply" goes when the shop's own form is not taking the application:
 * the merchant's own form when they gave one, else an email to them. The two
 * separately, so the role's page can offer both under its own form.
 */
export function externalApplyOptions(
  opening: Pick<StorefrontJobOpening, 'apply_url' | 'apply_email'>,
  subject: string,
): { url: string | null; mailto: string | null } {
  const url = opening.apply_url?.trim();
  const email = opening.apply_email?.trim();
  return {
    url: url && /^https?:\/\//i.test(url) ? url : null,
    mailto: email ? `mailto:${email}?subject=${encodeURIComponent(subject)}` : null,
  };
}

/** A calendar day (`YYYY-MM-DD`) or an ISO timestamp, as the shopper's language writes a date. */
export function formatCareerDate(value: string | null | undefined, locale: string): string | null {
  if (!value) return null;
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  // A bare day is a day on the store's calendar, not midnight UTC - read as
  // UTC it would show as the day before for anyone west of Greenwich.
  const date = day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

const SCHEMA_EMPLOYMENT: Record<EmploymentType, string> = {
  FULL_TIME: 'FULL_TIME',
  PART_TIME: 'PART_TIME',
  CONTRACT: 'CONTRACTOR',
  INTERNSHIP: 'INTERN',
  TEMPORARY: 'TEMPORARY',
  VOLUNTEER: 'VOLUNTEER',
};

/**
 * A schema.org `JobPosting` for one opening - what lets a search engine list
 * the role in its jobs results. Only what the merchant actually told us: no
 * invented salary range, no guessed address.
 */
export function jobPostingJsonLd(
  opening: StorefrontJobOpening,
  { storeName, url }: { storeName: string | null | undefined; url: string },
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: opening.title,
    // Plain words: the description may be the editor's HTML, and a
    // structured-data field is not the place for a shop's markup.
    description: richTextToPlain(opening.description) || opening.summary || opening.title,
    ...(opening.posted_at ? { datePosted: opening.posted_at.slice(0, 10) } : {}),
    ...(opening.closes_at ? { validThrough: opening.closes_at } : {}),
    employmentType: SCHEMA_EMPLOYMENT[opening.employment_type],
    ...(storeName ? { hiringOrganization: { '@type': 'Organization', name: storeName } } : {}),
    ...(opening.workplace_type === 'REMOTE' ? { jobLocationType: 'TELECOMMUTE' } : {}),
    ...(opening.location
      ? { jobLocation: { '@type': 'Place', address: { '@type': 'PostalAddress', addressLocality: opening.location } } }
      : {}),
    ...(opening.department ? { occupationalCategory: opening.department } : {}),
    url,
  };
}

/** Puts the opening's `JobPosting` in the head while its page is open. */
export function useJobPostingJsonLd(
  opening: StorefrontJobOpening | undefined,
  storeName: string | null | undefined,
) {
  const data = opening
    ? JSON.stringify(jobPostingJsonLd(opening, { storeName, url: window.location.href }))
    : null;

  useEffect(() => {
    if (!data) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.storefront = 'job-posting';
    // `<` escaped so a title containing `</script>` cannot end the tag.
    script.textContent = data.replace(/</g, '\\u003c');
    document.head.appendChild(script);
    return () => script.remove();
  }, [data]);
}
